/**
 * CacheKey — file identity → a stable name for its derived assets.
 * Pure module: no imports, no I/O.
 *
 * Two files with the same key are interchangeable; change any input (the file
 * was replaced, the target size changed) and the old cache entry is orphaned
 * rather than silently reused.
 */

export interface CacheKeyInput {
  sourceId: number;
  relPath: string;
  sizeBytes: number | null;
  modifiedAt: number | null;
  kind: 'thumb' | 'preview';
  targetPx: number;
}

/** FNV-1a, 32-bit. Fast, dependency-free, and plenty for cache naming. */
export function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function cacheKey(input: CacheKeyInput): string {
  const parts = [
    input.sourceId,
    input.relPath,
    input.sizeBytes ?? 0,
    input.modifiedAt ?? 0,
    input.targetPx,
  ].join(' ');

  return `${input.kind}_${input.targetPx}_${fnv1a(parts)}`;
}
